import type {
  LayerPath,
  TmsLayer,
  GisLayerItem,
  GisGeojsonLayer,
  GisTmsLayer,
  LineLayerItem,
} from "src/domains/shared/api/gisLayer/gisLayer.model";
import { normalizeToNFD } from "src/domains/shared/utils/normalizeToNFD";
export type LayerGroup = "gis-geojson" | "gis-tms" | "line";

// TMS 타일 경로(tiles)에서 레이어 이름 디렉토리 세그먼트가 위치하는 인덱스.
// path.split("/")[TMS_LAYER_NAME_SEGMENT_INDEX] 가 레이어 이름이 되도록 약속한다.
// 2D/3D 의 모든 tiles 경로 파싱이 이 상수를 참조해야 경로 구조 변경 시 한 곳만 고치면 된다.
export const TMS_LAYER_NAME_SEGMENT_INDEX = 4;

// 신규 2D id 식별자 prefix. identity 생성 함수와 컴포넌트 fallback 이 같은 값을 써야
// checkedKeys.has() 매칭이 어긋나지 않는다.
export const GIS_LAYER_ID_PREFIX = "2d-gis:";
export const LINE_LAYER_ID_PREFIX = "2d-line:";

// 색 정보가 없는 레이어(TMS 등)의 기본 표시/전송 색.
// UI 스와치 표시 경로와 mutation 전송 경로가 같은 값을 써야 화면 색과 서버 색이 어긋나지 않는다.
export const TMS_LAYER_DEFAULT_COLOR = "#ffffff";

// 파일명에서 "첫 점 이전 세그먼트"를 추출하는 name 파생 규칙.
// useDxfLineData 의 name 생성과 LineLayerList 의 매칭 키 생성이 반드시 같은 규칙을 써야
// 두 컬렉션의 find(l.name === ...) 매칭이 깨지지 않는다 (stemOfPath = 마지막 확장자 제거와는 다른 규칙).
export function firstSegmentBeforeDot(name: string): string {
  return name.split(".")[0];
}

// 파일 경로에서 레이어 이름(마지막 세그먼트) 추출 (2D/3D 공용 — 식별자 생성 헬퍼와 같은 위치에 둠)
export const extractLayerName = (path: string | undefined): string | null => {
  if (!path) return null;
  return path.split("/").pop() || null;
};

// 경로의 특정 인덱스 세그먼트(중간 디렉토리 등)를 추출.
// extractLayerName 과 달리 파일명이 아니라 path.split("/")[index] 를 반환하므로
// 호출부가 "파일명이 아닌 경로 세그먼트를 받는다"는 것을 함수명으로 알 수 있다.
export const extractPathSegment = (
  path: string | undefined,
  index: number
): string | null => {
  if (!path) return null;
  return path.split("/")[index] || null;
};

export interface LayerIdentity {
  id: string;
  name: string;
  group: LayerGroup;
}

export function stripExtension(name: string): string {
  return name.replace(/\.[^.]+$/i, "");
}

// 경로에서 확장자를 제거한 파일명 stem 반환 (basename 추출 후 마지막 확장자 제거)
export function stemOfPath(path: string | null | undefined): string {
  const base = extractLayerName(path ?? undefined);
  return base ? stripExtension(base) : "";
}

// 경로에서 파일명을 뽑은 뒤 "첫 점 이전 세그먼트"를 반환 (firstSegmentBeforeDot 의 경로 버전).
// LineLayerList 가 json 경로에서 매칭 키를 만들 때, useDxfLineData 의 name 파생 규칙과
// 동일하게 맞추기 위해 사용한다.
export function firstSegmentBeforeDotOfPath(
  path: string | null | undefined
): string {
  const base = extractLayerName(path ?? undefined);
  return base ? firstSegmentBeforeDot(base) : "";
}

export function getGeojsonLayerIdentity(
  layer: LayerPath
): LayerIdentity | null {
  const layerName = extractLayerName(layer.json);
  if (!layerName) return null;
  return {
    id: `gis-geojson:${layerName}`,
    name: stripExtension(layerName),
    group: "gis-geojson",
  };
}

export function getTmsLayerIdentity(layer: TmsLayer): LayerIdentity | null {
  const layerName = extractPathSegment(layer.tiles, TMS_LAYER_NAME_SEGMENT_INDEX);
  if (!layerName) return null;
  return {
    id: `gis-tms:${layerName}`,
    name: layerName,
    group: "gis-tms",
  };
}

export function getLinearLayerIdentity(
  layer: LayerPath
): LayerIdentity | null {
  const layerName = extractLayerName(layer.json);
  if (!layerName) return null;
  return {
    id: `line:${layerName}`,
    name: stripExtension(layerName),
    group: "line",
  };
}

// ─── 신규 2D id 기반 식별 헬퍼 ──────────────────────────────────────────────

// name 은 두 타입 모두 "확장자를 제거한 표시용 이름" 으로 통일한다.
// 사용자 지정 display_name 이 있으면 최우선, null 이면 기존 파일명 파생으로 폴백.
// tms: 타일 경로의 디렉토리 세그먼트(인덱스 4), geojson: json 파일명 stem.
// 둘 다 stripExtension 을 거쳐 같은 성격(표시명)을 갖도록 보장한다.
// id 공식은 display_name 과 무관하게 불변 — 행 key·삭제 선택·드래그 식별이 rename 에 영향받지 않는다.
// 반환 타입은 같은 함수군(getGeojsonLayerIdentity 등)과 동일하게 nullable 로 통일한다.
export function getGisLayerIdentity(layer: GisLayerItem): LayerIdentity | null {
  const rawName =
    layer.type === "tms_layer"
      ? extractPathSegment(
          (layer as GisTmsLayer).tiles ?? undefined,
          TMS_LAYER_NAME_SEGMENT_INDEX
        )
      : extractLayerName((layer as GisGeojsonLayer).json);
  const name =
    layer.display_name ?? stripExtension(rawName ?? String(layer.id));
  return {
    id: `${GIS_LAYER_ID_PREFIX}${layer.id}`,
    name,
    group: layer.type === "tms_layer" ? "gis-tms" : "gis-geojson",
  };
}

export function getLineLayerIdentity2d(
  layer: LineLayerItem
): LayerIdentity | null {
  const name =
    layer.display_name ??
    stripExtension(extractLayerName(layer.json) ?? String(layer.id));
  return {
    id: `${LINE_LAYER_ID_PREFIX}${layer.id}`,
    name,
    group: "line",
  };
}

// ─── 2D 표시 옵션 쓰기 대상 (type 쿼리 파라미터 + file_name stem) ─────────────
// `POST 2d-layer-options?type=&file_name=` 가 레이어를 가리키는 좌표이자, 프로젝트 데이터 폴더가 2D 항목을
// 부르는 이름(spec: project-data-folders — 폴더 항목 식별 키)이다. 옵션 쓰기와 폴더 키가 이 파생 하나를 쓴다.

/** 선형 레이어(line_layer)의 (type 쿼리 파라미터, fileStem) — 서버가 type 을 잘못 내려도 배열 위치로 선형이다 */
export function getLineLayerTypeAndStem(
  layer: LineLayerItem
): { typeParam: string; fileStem: string } {
  return {
    typeParam: "linear_layer",
    fileStem: stemOfPath(layer.json) || stemOfPath(layer.dxf),
  };
}

/** gis 레이어(gis_layer: tms/geojson)의 (type 쿼리 파라미터, fileStem) */
export function getGisLayerTypeAndStem(
  layer: GisLayerItem
): { typeParam: string; fileStem: string } {
  if (layer.type === "tms_layer") {
    const l = layer as GisTmsLayer;
    return {
      typeParam: "tms_layer",
      fileStem: stemOfPath(l.tif) || stemOfPath(l.tiles),
    };
  }
  const l = layer as GisGeojsonLayer;
  return {
    typeParam: "geojson_layer",
    fileStem: stemOfPath(l.json) || stemOfPath(l.dxf),
  };
}

// ─── rename API 식별자 (gis·linear 의 origin_file_name) ──────────────────────
// `renameLayerDisplayName` 이 2D 레이어를 가리키는 origin_file_name = 서버 경로의 확장자 제외 stem
// (spec: inspection2d-layer-management — 레이어 rename API 계약). 2D·3D GIS·선형 목록 훅의
// toRenamePayload 와 `gisLayer.queries` 의 낙관 갱신 매칭(applyDisplayNameChange)이 함께 읽는다 —
// 둘이 갈리면 낙관 갱신이 payload 가 가리킨 행을 찾지 못한다.
// 위 get*LayerTypeAndStem(geojson·선형은 json 우선, tms 는 tiles 폴백)과는 파생이 다른 별개 계약이다.
// 삭제 API layer_name 도 spec 상 같은 파생이지만, 기존 LayerPath/TmsLayer 를 받는
// `InspectionRepository.deleteGisLayerFiles`·`deleteLinearLayerFiles` 는 이 헬퍼를 거치지 않는다.

/** gis 레이어의 rename 식별자 — tms 는 tif, geojson 은 dxf 경로의 stem */
export function gisLayerRenameStem(layer: GisLayerItem): string {
  return layer.type === "tms_layer"
    ? stemOfPath(layer.tif)
    : stemOfPath(layer.dxf);
}

/** 선형 레이어의 rename 식별자 — dxf 경로의 stem */
export function lineLayerRenameStem(layer: Pick<LineLayerItem, "dxf">): string {
  return stemOfPath(layer.dxf);
}

// ─── 삭제 API 페이로드 매핑 (신규 2D 타입 → 기존 LayerPath/TmsLayer) ─────────
// 삭제 API 는 기존 LayerPath/TmsLayer 계약을 받으므로 신규 2D 레이어 타입에서 필드 매핑한다.

export function toGeojsonLayerPath(layer: GisGeojsonLayer): LayerPath {
  return {
    checked: layer.show,
    build_complete: layer.build_complete,
    json: layer.json,
    dxf: layer.dxf,
    color: layer.color,
    priority: 0,
  } as LayerPath;
}

export function toTmsLayer(layer: GisTmsLayer): TmsLayer {
  return {
    build_complete: layer.build_complete,
    checked: layer.show,
    tif: layer.tif ?? "",
    tiles: layer.tiles ?? "",
    matrix: layer.matrix ?? {},
    zoom_level: layer.zoom_level ?? { min: "0", max: "20" },
    priority: 0,
  } as TmsLayer;
}

export function toLineLayerPath(layer: LineLayerItem): LayerPath {
  return {
    checked: layer.show,
    build_complete: layer.build_complete,
    json: layer.json,
    dxf: layer.dxf,
    color: layer.color,
    priority: 0,
  } as LayerPath;
}

/**
 * 단면 레이어 값(`도면|파생 파일`)을 `multi/layers` step 모드가 받는 `drawing_filename`·`derived_file_name`
 * 쌍으로 바꾼다 — 파생 파일은 마지막 확장자를 떼고, 둘 다 NFD 로 정규화한다. `|` 가 없거나 한쪽이 비면 null.
 * 현재 스테이션 추출의 쿼리와 전체 추출 작업의 시리즈가 같은 값을 보내도록 이 한 곳을 쓴다.
 */
export function sectionLayerFileNames(
  layerName: string
): { drawingFilename: string; derivedFileName: string } | null {
  if (!layerName.includes("|")) return null;
  const [drawingFilename, derivedPart] = layerName.split("|");
  const derivedFileName = derivedPart?.replace(/\.[^/.]+$/, "") ?? derivedPart;
  if (!drawingFilename || !derivedFileName) return null;
  return {
    drawingFilename: normalizeToNFD(drawingFilename),
    derivedFileName: normalizeToNFD(derivedFileName),
  };
}
